<template>
  <a-modal
    v-model:visible="visible"
    :title="data.mode == 'create' ? '创建表盘' : '编辑表盘'"
    :after-close="onClose"
    :width="370"
    :body-style="{
      maxHeight: '700px',
      overflow: 'auto'
    }"
  >
    <form-watchface-manage
      ref="form"
      :path="data.path"
      :disabled-keys="data.mode == 'edit' ? ['shape'] : []"
      :mode="data.mode == 'create' ? 'simple' : 'complex'"
      :samples="samples"
    ></form-watchface-manage>
    <template #footer>
      <div class="flex justify-center mt-4 w-full px-8">
        <a-button key="back" class="" @click="() => (visible = false)">
          关闭
        </a-button>
        <a-button
          v-if="data.mode == 'edit' && ElectronApi.isNormal()"
          key="preview"
          type="primary"
          class="ml-2.5"
          :loading="loading"
          @click="form?.preview"
        >
          预览
        </a-button>
        <a-button
          key="submit"
          type="primary"
          class="ml-2.5"
          :loading="loading"
          @click="onSubmit"
        >
          {{ data.mode == 'create' ? '创建' : '保存' }}
        </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script lang="ts" setup>
import { reactive, ref } from 'vue';
import {
  WatchfaceFormData,
  useWatchfaceAndroid,
  useWatchfaceCorrectData,
  useWatchfaceManage
} from '@/hooks';
import { nextTick } from 'vue';
import { Modal, notification } from 'ant-design-vue';
import { createDialTrack } from '@/utils/track/app';
import { canUse } from '@/utils/can-use.utils';
import {
  getWatchfaceDefaultId,
  getWatchfaceRandomId
} from '@/utils/watchface-id.utils';
import { SampleService } from '@/services/sample.service';
import { cloneDeep } from 'lodash-es';
import { useEditorStore } from '@/store/use-editor-store';
import { Introduce } from '@/types/watchface';
import { ElectronApi } from '@/services/electron.api';

const form = ref();
const { createWatchface, editWatchface, gotoWachfaceEditor } =
  useWatchfaceManage();

const { checkIsAndroid } = useWatchfaceAndroid();
const { correctWatchfaceStore, correctLayerStore } = useWatchfaceCorrectData();
const editorStore = useEditorStore();

const visible = ref(false);
const loading = ref(false);
const samples = ref<string[]>([]);
// 打开或关闭modal
const data = reactive<{
  path?: string;
  mode?: 'create' | 'edit';
}>({});

const onEnter = (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    submit();
  }
};

const open = async (_data?: { path?: string; mode?: 'create' | 'edit' }) => {
  data.mode = _data?.mode ?? 'create';
  data.path = _data?.path;
  watchfacePathCreated.value = '';
  // console.log('open', _data?.path);
  visible.value = true;
  await nextTick();
  form.value?.initData?.(data.path);
  form.value.$el.removeEventListener('keydown', onEnter);
  form.value.$el.addEventListener('keydown', onEnter);
  if (data.mode === 'edit') { // 当前是编辑表盘的情况，需要获取到表盘样例列表
    const service = new SampleService();
    const list = await service.getSamples();
    list?.forEach((item) => {
      samples.value.push(item.deviceType)
    })
  }
};
defineExpose({ open });

const onClose = () => {
  form.value?.clear();
  loading.value = false;
  if (watchfacePathCreated.value) {
    gotoWachfaceEditor(watchfacePathCreated.value);
    // notification.success({
    //   message: '表盘新建成功'
    // });
  }
};

const watchfacePathCreated = ref('');

const submit = async (modifyDevice?: boolean) => {
  const res = await form.value?.getData();
  if (!res?.valid) return;
  const formData = cloneDeep(res.data) as WatchfaceFormData;
  const introData = cloneDeep(res.introData) as Introduce;

  const deviceType = formData.deviceType;
  if (!deviceType) return;
  try {
    loading.value = true;
    watchfacePathCreated.value = '';
    // 校验watchOS
    if (formData.watchOS == 'android' && !checkIsAndroid(deviceType)) {
      formData.watchOS = 'vela';
    }
    if (data.mode == 'edit' && data.path) {
      // 编辑表盘信息
      const res = await editWatchface(data.path, formData, introData);
      if (modifyDevice) {
        correctWatchfaceStore();
        correctLayerStore();
        editorStore.selectTheme();
      }
      if (res) {
        notification.success({
          message: '表盘修改成功'
        });
        visible.value = false;
      }
    } else {
      // 表盘创建
      // 新建表盘时，设置pkgName的值
      const canUseAutoId = canUse('auto-watchface-id', deviceType).valid;
      formData.pkgName = canUseAutoId
        ? getWatchfaceDefaultId()
        : getWatchfaceRandomId();

      const watchfacePath = await createWatchface(formData);
      createDialTrack({
        dial_name: formData.name,
        device_model: formData.deviceType as string,
        device_size: formData.size
      });
      if (watchfacePath) {
        watchfacePathCreated.value = watchfacePath;
        visible.value = false;
      }
    }
  } catch (error) {
    console.log('表盘新建或修改错误', error);
    watchfacePathCreated.value = '';
    notification.warn({
      message: '操作失败',
      description: String(error)
    });
  } finally {
    loading.value = false;
  }
};

const onSubmit = async () => {
  const res = await form.value?.getData();
  if (!res?.valid) return;
  // 是否修改了设备型号
  const modifyDevice = !!res?.modifyDevice as boolean;
  if (modifyDevice && data.mode == 'edit') {
    Modal.confirm({
      title: '修改设备型号？',
      content: '不同设备之间表盘能力有差异，是否修改？',
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        submit(true);
      }
    });
  } else {
    submit();
  }
};
</script>

<style lang="less"></style>
